import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronRight, ChevronLeft, Check, Sparkles } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { PILLARS } from '@/constants/pillars';
import { ASSESSMENT } from '@/constants/testIds';

export default function Assessment() {
	const navigate = useNavigate();
	const [questions, setQuestions] = useState([]);
	const [pillars, setPillars] = useState(PILLARS);
	const [step, setStep] = useState(0);
	const [answers, setAnswers] = useState({});
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get('/config/questions');
				setQuestions(data.questions || []);
				if (data.pillars) {
					// keep our local rich pillar metadata; only sync ordering
				}
			} catch (err) {
				toast.error(formatApiError(err));
			}
		})();
	}, []);

	const currentQuestion = questions[step];
	const total = questions.length;
	const pct = total ? Math.round(((step + (currentQuestion && answers[currentQuestion.id] != null ? 1 : 0)) / total) * 100) : 0;
	const currentPillar = useMemo(
		() => pillars.find((p) => p.key === currentQuestion?.pillar) || pillars[0],
		[pillars, currentQuestion],
	);

	const setAnswer = (val) => {
		setAnswers((a) => ({ ...a, [currentQuestion.id]: val }));
	};

	const next = () => {
		if (answers[currentQuestion.id] == null) {
			toast.error('Pick the option that best fits your family');
			return;
		}
		if (step + 1 < total) setStep(step + 1);
	};
	const prev = () => setStep((s) => Math.max(0, s - 1));

	const submit = async () => {
		if (!total) return;
		const missing = questions.filter((q) => answers[q.id] == null);
		if (missing.length) {
			toast.error(`${missing.length} question${missing.length > 1 ? 's' : ''} still to answer`);
			setStep(questions.findIndex((q) => q.id === missing[0].id));
			return;
		}
		setSubmitting(true);
		try {
			const { data } = await api.post('/assessments', { answers });
			toast.success(`Your Family Resilience Score is ${data.overall}`);
			navigate(`/score/${data.assessment_id}`);
		} catch (err) {
			toast.error(formatApiError(err));
		} finally {
			setSubmitting(false);
		}
	};

	if (!total) {
		return (
			<Layout>
				<div className="card-soft p-10 text-center text-slate-500">Preparing your assessment…</div>
			</Layout>
		);
	}

	const isLast = step === total - 1;
	const Icon = currentPillar.icon;

	return (
		<Layout>
			<div data-testid={ASSESSMENT.root} className="max-w-3xl mx-auto">
				<div className="mb-6">
					<div className="flex items-center justify-between mb-3">
						<div className="flex items-center gap-3">
							<div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${currentPillar.accent} text-white grid place-items-center shadow-sm`}>
								<Icon className="w-5 h-5" />
							</div>
							<div>
								<div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Pillar</div>
								<div className="font-heading text-lg font-semibold text-slate-900">{currentPillar.title}</div>
							</div>
						</div>
						<div className="text-sm text-slate-500 tabular-nums">{step + 1} / {total}</div>
					</div>
					<Progress value={pct} className="h-2 rounded-full" data-testid={ASSESSMENT.progress} />
				</div>

				<div className="card-soft p-8">
					<h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight text-slate-900 leading-snug">
						{currentQuestion.text}
					</h2>

					<div className="mt-6 space-y-3">
						{currentQuestion.options.map((opt, idx) => {
							const selected = answers[currentQuestion.id] === idx;
							return (
								<button
									key={idx}
									type="button"
									onClick={() => setAnswer(idx)}
									data-testid={`${ASSESSMENT.option}-${idx}`}
									className={`w-full text-left flex items-center gap-4 px-5 py-4 rounded-2xl border-2 transition-all ${
										selected
											? 'border-emerald-500 bg-emerald-50 shadow-glow-emerald'
											: 'border-slate-200 hover:border-slate-300 bg-white'
									}`}
								>
									<span className={`w-6 h-6 rounded-full grid place-items-center text-xs font-bold ${selected ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
										{selected ? <Check className="w-3.5 h-3.5" /> : String.fromCharCode(65 + idx)}
									</span>
									<span className="font-medium text-slate-800">{opt}</span>
								</button>
							);
						})}
					</div>

					<div className="mt-8 flex items-center justify-between">
						<Button
							type="button"
							variant="ghost"
							onClick={prev}
							disabled={step === 0}
							data-testid={ASSESSMENT.prevButton}
							className="rounded-full"
						>
							<ChevronLeft className="w-4 h-4 mr-1" /> Back
						</Button>
						{isLast ? (
							<Button
								type="button"
								onClick={submit}
								disabled={submitting}
								data-testid={ASSESSMENT.submitButton}
								className="rounded-full bg-emerald-600 hover:bg-emerald-700 px-6 h-11 font-semibold gap-2"
							>
								<Sparkles className="w-4 h-4" /> {submitting ? 'Calculating…' : 'See my Family Score'}
							</Button>
						) : (
							<Button
								type="button"
								onClick={next}
								data-testid={ASSESSMENT.nextButton}
								className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white px-6 h-11"
							>
								Next <ChevronRight className="w-4 h-4 ml-1" />
							</Button>
						)}
					</div>
				</div>
			</div>
		</Layout>
	);
}
